import React from "react";

/**
 * Interface for RTK Query error object structure
 */
export interface RTKQueryError {
  status?: number;
  error?: string;
  data?:
    | {
        message?: string;
        [key: string]: any;
      }
    | string;
}

/**
 * Props for the RTKQErrorCard component
 */
export interface RTKQErrorCardProps {
  error: RTKQueryError | null;
  title?: string;
  className?: string;
  compact?: boolean;
  maxHeight?: string | number;
  showFullDetails?: boolean;
}

/**
 * Format RTK Query error data into a readable string for debugging
 *
 * @param errorObj - The error object from RTK Query
 * @returns Formatted error data as a string
 */
function formatErrorData(errorObj: RTKQueryError): string {
  const errorDetails: string[] = [];

  // Add status if available
  if (errorObj.status) {
    errorDetails.push(`Status: ${errorObj.status}`);
  }

  // Add error message if available as string
  if (typeof errorObj.error === "string") {
    errorDetails.push(`Error: ${errorObj.error}`);
  }

  // Process data property (could be an object, string, or undefined)
  if (errorObj.data) {
    if (typeof errorObj.data === "object") {
      // Format object properties
      errorDetails.push(`Data:`);
      Object.entries(errorObj.data).forEach(([key, value]) => {
        const valueStr =
          typeof value === "object" ? JSON.stringify(value) : String(value);
        errorDetails.push(`  ${key}: ${valueStr}`);
      });
    } else {
      // If data is a string or other primitive
      errorDetails.push(`Data: ${String(errorObj.data)}`);
    }
  }

  return errorDetails.join("\n");
}

/**
 * Reusable component for displaying API error data consistently
 */
const RTKQErrorCard: React.FC<RTKQErrorCardProps> = ({
  error,
  title = "Error", // Default title is now a simple string
  className = "",
  compact = false,
  maxHeight = "200px",
  showFullDetails = true,
}) => {
  if (!error) return null;

  const cardTitle = title;
  const statusCode = error.status || null;
  const formattedTitle = statusCode
    ? `${cardTitle} (${statusCode})`
    : cardTitle;
  const simpleErrorMessage =
    (error.data && typeof error.data === "object" && error.data.message) ||
    (typeof error.error === "string" ? error.error : "Unknown error");
  const formattedErrorData = formatErrorData(error);

  return (
    <div className={`error-data ${compact ? "compact" : ""} ${className}`}>
      <div className="error-data__header">
        <h4 className="error-data__title">{formattedTitle}</h4>
        {!compact && (
          <span className="error-data__badge">{statusCode || "Error"}</span>
        )}
      </div>

      <div
        className="error-data__content"
        style={{ maxHeight: showFullDetails ? maxHeight : "auto" }}
      >
        <div className="error-data__message">{simpleErrorMessage}</div>
        {showFullDetails && (
          <pre className="error-data__details">{formattedErrorData}</pre>
        )}
      </div>
    </div>
  );
};

export default RTKQErrorCard;
